import React, { useEffect, useState } from 'react';
import { useUserProfile, UserAvatarInfo } from '../../hooks/useUserProfile';

/**
 * Atténue une couleur hex en réduisant son intensité
 * @param color Couleur au format hex (#RRGGBB)
 * @param percent Pourcentage d'atténuation (0-100)
 * @returns Couleur atténuée
 */
const softendColor = (color: string, percent: number = 15): string => {
  // Si la couleur n'est pas au format hex, retourner telle quelle
  if (!color || !color.startsWith('#')) {
    return color;
  }
  
  try {
    // Convertir hex en RGB
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    
    // Atténuer en ajoutant du blanc (en augmentant les valeurs)
    const factor = percent / 100;
    const newR = Math.min(255, r + Math.round((255 - r) * factor));
    const newG = Math.min(255, g + Math.round((255 - g) * factor));
    const newB = Math.min(255, b + Math.round((255 - b) * factor));
    
    // Convertir en hex et retourner
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  } catch (error) {
    console.error('Erreur lors de l\'atténuation de la couleur:', error);
    return color; // En cas d'erreur, retourner la couleur d'origine
  }
};

interface UserAvatarProps {
  userId: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Composant réutilisable pour afficher l'avatar d'un utilisateur
 * Utilise les mêmes styles et logique que les avatars des marqueurs sur la carte
 */
const UserAvatar: React.FC<UserAvatarProps> = ({ userId, size = 22, className, style }) => {
  const { getUserInfo } = useUserProfile();
  const [userInfo, setUserInfo] = useState<UserAvatarInfo | null>(null);
  
  // Effet pour récupérer et mettre à jour les informations utilisateur
  useEffect(() => {
    if (!userId) return;
    
    console.log('🔍 UserAvatar - Tentative de récupération des infos pour:', userId);
    const info = getUserInfo(userId);
    setUserInfo(info);
    
    // Vérification périodique pour une mise à jour des infos
    const checkForUpdates = setInterval(() => {
      const latestInfo = getUserInfo(userId);
      if (latestInfo && (!userInfo || JSON.stringify(latestInfo) !== JSON.stringify(userInfo))) {
        console.log('🔄 UserAvatar - Mise à jour des infos pour:', userId);
        setUserInfo(latestInfo);
      }
    }, 1000); // Vérifier toutes les secondes pendant le chargement initial
    
    return () => clearInterval(checkForUpdates);
  }, [userId, getUserInfo, userInfo]);
  
  // Log pour le débogage
  useEffect(() => {
    console.log('📝 UserAvatar - État actuel:', { userId, userInfo });
  }, [userId, userInfo]);

  // Si pas d'info, afficher un placeholder de chargement
  if (!userInfo) {
    console.log('⚠️ UserAvatar - Aucune information utilisateur disponible pour:', userId);
    return (
      <div
        className={className}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          backgroundColor: '#e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: `${size * 0.4}px`,
          border: '1px solid #ccc',
          ...style
        }}
      >
        ?
      </div>
    );
  }

  // Si en chargement, afficher un indicateur
  if (userInfo.isLoading) {
    return (
      <div
        className={className}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: `${size * 0.4}px`,
          border: '1px solid #ddd',
          ...style
        }}
      >
        ⏳
      </div>
    );
  }

  // Avatar normal avec les informations utilisateur
  const avatarSize = size;
  const avatarStyle: React.CSSProperties = {
    width: `${avatarSize}px`,
    height: `${avatarSize}px`,
    borderRadius: '50%',
    border: `2px solid ${softendColor(userInfo.color || '#4285F4')}`,
    outline: '1px solid rgba(0,0,0,0.3)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    backgroundColor: softendColor(userInfo.color || '#4285F4'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: `${avatarSize * 0.45}px`,
    fontWeight: 'bold',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    ...(userInfo.photo_url ? { backgroundImage: `url(${userInfo.photo_url})` } : {}),
    ...style
  };

  return (
    <div className={className} style={avatarStyle}>
      {!userInfo.photo_url ? userInfo.initials || 'U' : ''}
    </div>
  );
};

export default UserAvatar;

